import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { GraduationCap, LogOut, Menu, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { LABELS } from '@orienta/shared';
import { useAuthed, useSession } from '@/features/auth/session';
import { useInstitutionContext } from '@/lib/institution-context';
import { useTheme, type ThemeChoice } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/domain/UserAvatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BreadcrumbProvider, Breadcrumbs } from './breadcrumbs';
import { navItemsFor, type ResolvedNavItem } from './nav';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { CommandPalette } from '@/features/search/CommandPalette';
import { ViewAsBanner } from '@/features/admin/ViewAs';

const SECTION_LABEL: Record<ResolvedNavItem['section'], string> = { principal: 'Acompanhamento', gestao: 'Administração', conta: 'Conta' };

/** Menu vertical (celular e tablet, dentro do Sheet). */
function NavList({ items, onNavigate }: { items: ResolvedNavItem[]; onNavigate?: () => void }) {
  const sections = (['principal', 'gestao', 'conta'] as const).filter((s) => items.some((i) => i.section === s));
  return (
    <nav aria-label="Navegação principal" className="flex flex-col gap-4">
      {sections.map((section) => (
        <div key={section} className="flex flex-col gap-1">
          <p className="text-muted-foreground px-3 text-xs font-semibold tracking-wide uppercase">{SECTION_LABEL[section]}</p>
          {items
            .filter((i) => i.section === section)
            .map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn('hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors', isActive ? 'bg-primary/10 text-primary' : 'text-foreground')
                }
              >
                <item.icon className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{item.label}</span>
              </NavLink>
            ))}
        </div>
      ))}
    </nav>
  );
}

/**
 * Menu horizontal da barra superior (desktop). Notificações e perfil ficam no sino e no menu do usuário.
 * Com muitos itens (ex.: gestão institucional legada), rola na horizontal em vez de empurrar o menu do usuário para fora da tela.
 */
function TopNav({ items }: { items: ResolvedNavItem[] }) {
  return (
    <nav aria-label="Navegação principal" className="hidden min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] lg:flex">
      {items
        .filter((i) => i.section !== 'conta')
        .map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'hover:bg-accent hover:text-accent-foreground flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors',
                isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
              )
            }
          >
            <item.icon className="size-4 shrink-0" aria-hidden />
            {item.label}
          </NavLink>
        ))}
    </nav>
  );
}

function Brand() {
  return (
    <span className="flex items-center gap-2">
      <GraduationCap className="size-6 shrink-0" aria-hidden />
      <span className="text-xl font-bold tracking-tight">Orienta</span>
    </span>
  );
}

function ContextSelector({ className }: { className?: string }) {
  const { memberships, institutionId, setInstitutionId } = useInstitutionContext();
  // Um único contexto (ex.: só o espaço de orientação): nada para escolher.
  if (memberships.length <= 1) return null;
  return (
    <Select value={institutionId ?? 'all'} onValueChange={(v) => setInstitutionId(v === 'all' ? null : v)}>
      <SelectTrigger size="sm" className={cn('w-56', className)} aria-label="Instituição em contexto">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {memberships.length > 1 && <SelectItem value="all">Todas as instituições</SelectItem>}
        {memberships.map((m) => (
          <SelectItem key={m.institutionId} value={m.institutionId}>
            {m.acronym} — {m.institutionName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function UserMenu() {
  const session = useAuthed();
  const { logout } = useSession();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const roles = [...new Set(session.memberships.flatMap((m) => m.roles.map((r) => LABELS.role[r.role])))];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 gap-2 rounded-full p-0 xl:h-10 xl:px-1 xl:pr-3">
          <span className="sr-only">Menu do usuário</span>
          <UserAvatar name={session.user.name} url={session.user.avatarUrl} aria-hidden />
          {/* Nome ao lado da foto, como no votaDCR, quando a barra tem espaço. */}
          <span aria-hidden className="hidden max-w-40 truncate text-sm font-medium xl:inline">
            {session.user.name}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate font-medium">{session.user.name}</p>
          <p className="text-muted-foreground truncate text-xs">{session.user.email}</p>
          {roles.length > 0 && <p className="text-muted-foreground mt-1 text-xs">{roles.join(' · ')}</p>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/perfil')}>
          <UserRound /> Perfil e preferências
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-muted-foreground text-xs">Tema</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemeChoice)}>
          <DropdownMenuRadioItem value="light">
            <Sun className="size-4" /> Claro
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="size-4" /> Escuro
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="size-4" /> Sistema
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={async () => {
            await logout();
            navigate('/entrar', { replace: true });
          }}
        >
          <LogOut /> Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell() {
  const session = useAuthed();
  const { institutionId } = useInstitutionContext();
  const items = navItemsFor(session, institutionId);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <BreadcrumbProvider>
      <a href="#conteudo" className="bg-primary text-primary-foreground sr-only z-50 rounded-md px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2">
        Pular para o conteúdo
      </a>
      <div className="flex min-h-svh flex-col">
        {/* Barra superior na cor principal; .topbar redefine os tokens para os controles sobre o fundo colorido (index.css). */}
        <header className="topbar bg-topbar text-topbar-foreground sticky top-0 z-30 shadow-md">
          <div className="mx-auto flex h-16 w-full max-w-screen-2xl items-center gap-2 px-3 sm:px-6 lg:px-8">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
              <Menu />
            </Button>
            <NavLink to="/" className="mr-4 shrink-0 rounded-md" aria-label="Orienta, ir para o painel">
              <Brand />
            </NavLink>
            <TopNav items={items} />
            <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
              <CommandPalette />
              {/* No celular o seletor fica no menu lateral: na barra, empurraria o sino e o perfil para fora da tela. */}
              <ContextSelector className="hidden sm:flex" />
              <NotificationBell />
              <UserMenu />
            </div>
          </div>
        </header>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-72 p-4">
            <SheetHeader className="p-0">
              <SheetTitle className="text-primary">
                <Brand />
              </SheetTitle>
              <SheetDescription className="sr-only">Menu de navegação</SheetDescription>
            </SheetHeader>
            <div className="sm:hidden">
              <ContextSelector className="w-full" />
            </div>
            <NavList items={items} onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        {session.impersonator && <ViewAsBanner />}
        <main id="conteudo" tabIndex={-1} className="mx-auto w-full max-w-screen-2xl flex-1 space-y-6 p-4 outline-none sm:p-6 lg:p-8">
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
    </BreadcrumbProvider>
  );
}
